/**
 * `PageMeta` as tags.
 *
 * One list of tag *descriptions*, serialised two ways: to a string of HTML for
 * the build-time prerenderer, and to real DOM nodes by `apps/web`'s `Seo`
 * component as the student navigates. Defining the tags once is the point — a
 * page whose static HTML and live document disagree about its canonical URL is
 * worse than one with no canonical at all.
 */

import type { HeadTag, PageMeta } from './types.ts';
import { SITE, absoluteUrl } from './site.ts';

/**
 * The region of `index.html` the prerenderer rewrites.
 *
 * Everything between these comments is per-route and replaceable; the charset,
 * the viewport and the icon sit outside them and are never touched.
 */
export const HEAD_START = '<!-- seo:head -->';
export const HEAD_END = '<!-- /seo:head -->';

export function headTags(meta: PageMeta, origin: string = SITE.origin): HeadTag[] {
  const url = absoluteUrl(meta.path, origin);
  const image = absoluteUrl(SITE.image.path, origin);

  const tags: HeadTag[] = [
    { tag: 'title', text: meta.title },
    { tag: 'meta', attrs: { name: 'description', content: meta.description } },
    // `max-image-preview:large` is what lets a result carry the card image;
    // the other two say "the snippet is yours to write", which for a lesson
    // summary is exactly right.
    {
      tag: 'meta',
      attrs: {
        name: 'robots',
        content:
          meta.robots === 'noindex'
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1',
      },
    },
    { tag: 'link', attrs: { rel: 'canonical', href: url } },
    { tag: 'meta', attrs: { name: 'theme-color', content: SITE.themeColor } },

    { tag: 'meta', attrs: { property: 'og:site_name', content: SITE.name } },
    { tag: 'meta', attrs: { property: 'og:type', content: meta.kind } },
    { tag: 'meta', attrs: { property: 'og:title', content: meta.title } },
    { tag: 'meta', attrs: { property: 'og:description', content: meta.description } },
    { tag: 'meta', attrs: { property: 'og:url', content: url } },
    { tag: 'meta', attrs: { property: 'og:locale', content: SITE.ogLocale } },
    { tag: 'meta', attrs: { property: 'og:image', content: image } },
    { tag: 'meta', attrs: { property: 'og:image:width', content: String(SITE.image.width) } },
    { tag: 'meta', attrs: { property: 'og:image:height', content: String(SITE.image.height) } },
    { tag: 'meta', attrs: { property: 'og:image:alt', content: SITE.image.alt } },

    { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { tag: 'meta', attrs: { name: 'twitter:title', content: meta.title } },
    { tag: 'meta', attrs: { name: 'twitter:description', content: meta.description } },
    { tag: 'meta', attrs: { name: 'twitter:image', content: image } },
  ];

  for (const node of meta.jsonLd) {
    tags.push({ tag: 'jsonld', json: JSON.stringify(node) });
  }

  return tags;
}

/* -------------------------------------------------------------------------- */
/* Serialisation                                                              */
/* -------------------------------------------------------------------------- */

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escapes JSON for embedding in a `<script>`.
 *
 * A curriculum summary containing `</script>` would otherwise end the element
 * early and put lesson prose into the document as markup. Nothing in the
 * content does that today, which is precisely why it has to be handled here
 * rather than noticed later.
 */
function escapeJson(json: string): string {
  return json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
}

function renderAttrs(attrs: Record<string, string>): string {
  return Object.entries(attrs)
    .map(([name, value]) => ` ${name}="${escapeHtml(value)}"`)
    .join('');
}

/** The head region for one page, as indented HTML. */
export function renderHead(meta: PageMeta, origin: string = SITE.origin): string {
  return headTags(meta, origin)
    .map((tag) => {
      switch (tag.tag) {
        case 'title':
          return `    <title data-seo>${escapeHtml(tag.text)}</title>`;
        case 'meta':
          return `    <meta data-seo${renderAttrs(tag.attrs)} />`;
        case 'link':
          return `    <link data-seo${renderAttrs(tag.attrs)} />`;
        case 'jsonld':
          return `    <script data-seo type="application/ld+json">${escapeJson(tag.json)}</script>`;
      }
    })
    .join('\n');
}

/**
 * What a crawler sees before the application boots.
 *
 * `createRoot().render()` replaces the contents of `#root`, so this costs a
 * signed-in student nothing and is gone the moment React mounts. It is not
 * hidden text and not a different page: the heading, the lead and the links are
 * the same ones the rendered page shows. Its job is that a crawler which does
 * not execute JavaScript — and a student on a slow connection — still gets the
 * heading and a route onwards through the curriculum rather than a blank div.
 */
export function renderFallbackBody(meta: PageMeta): string {
  const links = meta.links
    .slice(0, 40)
    .map((link) => `          <li><a href="${escapeHtml(link.path)}">${escapeHtml(link.label)}</a></li>`)
    .join('\n');

  return [
    '      <div class="page page--reading">',
    `        <h1 class="page__title">${escapeHtml(meta.heading)}</h1>`,
    `        <p class="page__lead">${escapeHtml(meta.lead)}</p>`,
    links === '' ? '' : `        <ul class="prerender-links">\n${links}\n        </ul>`,
    '      </div>',
  ]
    .filter((line) => line !== '')
    .join('\n');
}

/**
 * Writes one page's head and fallback body into the built `index.html`.
 *
 * Throws rather than degrading if a marker is missing: a silent no-op here
 * would ship a site where every URL shares the homepage's title, and nobody
 * would find out until the search results did.
 */
export function injectSeo(template: string, parts: { head: string; body: string }): string {
  const start = template.indexOf(HEAD_START);
  const end = template.indexOf(HEAD_END);
  if (start === -1 || end === -1 || end < start) {
    throw new Error(`index.html is missing the ${HEAD_START} … ${HEAD_END} markers`);
  }

  const head = `${template.slice(0, start + HEAD_START.length)}\n${parts.head}\n  ${template.slice(end)}`;

  const rootPattern = /<div id="root">\s*<\/div>/;
  if (!rootPattern.test(head)) {
    throw new Error('index.html is missing an empty <div id="root"></div>');
  }

  return head.replace(rootPattern, `<div id="root">\n${parts.body}\n    </div>`);
}

/**
 * Where a route's prerendered file goes.
 *
 * `lessons/add-fractions-01.html`, not `lessons/add-fractions-01/index.html`,
 * and the difference is a redirect. Cloudflare Pages serves a directory index
 * only after a 308 to the trailing-slash form, so the directory layout would
 * make every canonical URL on the site — every sitemap entry, every internal
 * link, every link anyone shares — answer with a redirect to a URL the page
 * itself does not claim as canonical. The flat form is served at `/lessons/x`
 * directly, with a 200.
 */
export function outputPathFor(routePath: string): string {
  const clean = routePath.replace(/^\/+/, '').replace(/\/+$/, '');
  return clean === '' ? 'index.html' : `${clean}.html`;
}
